import { Injectable, inject } from '@angular/core';
import { ApiGatewayService } from '@wam/authentication';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AnalyticsFacets, Organization } from '../analytics.model';
import { AnalyticsQuery } from '../analytics-query.model';
import {
  AnalyticsSchool,
  ClassroomAnalytics,
  Facets,
  ParentHomeDocument,
  StudentAnalytics,
} from '@wam/shared';

type AnalyticsCollectionPayload<T> = {
  hits?: T[];
  totalHits?: number;
  facets?: AnalyticsFacets[];
};

type AnalyticsCollectionResponse<T> = AnalyticsCollectionPayload<T> & {
  data?: {
    attributes?: AnalyticsCollectionPayload<T>;
  };
  attributes?: AnalyticsCollectionPayload<T>;
};

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly apiGatewayService = inject(ApiGatewayService);

  private static readonly rolesDisplayValues = {
    teacher: 'Teacher',
    school_admin: 'School Admin',
    district_admin: 'District Admin',
  };

  static transformFacets(analyticsFacets: AnalyticsFacets[]): Facets {
    const facets: Facets = {};
    if (analyticsFacets) {
      analyticsFacets.forEach((userFacet) => {
        Object.keys(userFacet).forEach((key) => {
          facets[key] = userFacet[key].map((item) => {
            switch (key) {
              case 'schools':
              case 'classes':
              case 'tags':
                return { value: item.uuid, display: item.name };
              case 'unassignedStudents':
                return {
                  value: item,
                  display: item === 'true' ? 'Show Unlicensed' : 'Show Licensed',
                };
              case 'roles':
                return {
                  value: item,
                  display: this.rolesDisplayValues[item] ?? item,
                };
              case 'benchmarks':
              case 'benchmarkStatus':
              case 'courseAssignments':
                return { value: item.key, display: item.name };
              default:
                return { value: item, display: item };
            }
          });
        });
      });
    }
    return facets;
  }

  private static normalizeAnalyticsCollectionResponse<T>(
    response: AnalyticsCollectionResponse<T>,
  ): Required<AnalyticsCollectionPayload<T>> {
    const payload = response?.data?.attributes ?? response?.attributes ?? response;

    return {
      hits: payload?.hits ?? [],
      totalHits: payload?.totalHits ?? 0,
      facets: payload?.facets ?? [],
    };
  }

  getClasses(page: number): Observable<ClassroomAnalytics[]> {
    return this.apiGatewayService
      .get<AnalyticsCollectionResponse<ClassroomAnalytics>>(
        `analytics/v3/indexes/esd_wra_edu/views/classes/query?page=${page}&limit=100`,
      )
      .pipe(
        map(
          (response: AnalyticsCollectionResponse<ClassroomAnalytics>) =>
            AnalyticsService.normalizeAnalyticsCollectionResponse(response).hits,
        ),
      );
  }

  getClassesWithFacets(
    query: AnalyticsQuery,
    page: number,
    view = 'classes',
  ): Observable<[ClassroomAnalytics[], number, Facets]> {
    return this.apiGatewayService
      .get(
        `analytics/v3/indexes/esd_wra_edu/views/${view}/query?page=${page}&limit=100${this.queryString(
          query,
        )}`,
      )
      .pipe(
        // @ts-ignore
        take(1),
        map((response: AnalyticsCollectionResponse<ClassroomAnalytics>) => {
          const payload = AnalyticsService.normalizeAnalyticsCollectionResponse(response);

          return [
            payload.hits,
            payload.totalHits,
            AnalyticsService.transformFacets(payload.facets),
          ];
        }),
      );
  }

  getStudentsInClass(classId: string): Observable<StudentAnalytics[]> {
    return this.apiGatewayService
      .get<AnalyticsCollectionResponse<StudentAnalytics>>(
        `analytics/v3/indexes/esd_wra_users/views/users_students/query?classes=${classId}&limit=1000`,
      )
      .pipe(
        map(
          (response: AnalyticsCollectionResponse<StudentAnalytics>) =>
            AnalyticsService.normalizeAnalyticsCollectionResponse(response).hits,
        ),
      );
  }

  getUsers(
    query: AnalyticsQuery,
    page: number,
    view = 'users_students',
  ): Observable<[StudentAnalytics[], number, Facets]> {
    return this.apiGatewayService
      .get(
        `analytics/v3/indexes/esd_wra_users/views/${view}/query?page=${page}&limit=100${this.queryString(
          query,
        )}`,
      )
      .pipe(
        // @ts-ignore
        take(1),
        map((response: AnalyticsCollectionResponse<StudentAnalytics>) => {
          const payload = AnalyticsService.normalizeAnalyticsCollectionResponse(response);

          return [
            payload.hits,
            payload.totalHits,
            AnalyticsService.transformFacets(payload.facets),
          ];
        }),
      );
  }

  getUserCounts(
    role: string,
    organization: string,
    page = 1,
    school?: string,
  ): Observable<{ uuid: string; name: string; count: number }[]> {
    const orgCounts = (facets) => {
      if (!school)
        return [
          {
            uuid: facets[1].organization[0]?.key ?? organization,
            name: facets[1].organization[0]?.value ?? '',
            count: facets[1].organization[0]?.count ?? 0,
          },
        ];
      return [];
    };
    const schoolParam = school ? `&school=${school}` : '';
    return this.apiGatewayService
      .get(
        `analytics/v3/indexes/esd_wra_users/views/schools_users_count/query?roles=${role}&organization=${organization}${schoolParam}&limit=100&page=${page}`,
      )
      .pipe(
        // @ts-ignore
        map(
          (
            response: AnalyticsCollectionResponse<{ uuid: string; name: string; count: number }>,
          ) => {
            const payload = AnalyticsService.normalizeAnalyticsCollectionResponse(response);

            return [
              ...orgCounts(payload.facets),
              ...payload.hits.map(({ uuid, name, count }) => ({ uuid, name, count })),
            ];
          },
        ),
      );
  }

  getSchools(
    query: AnalyticsQuery,
    page: number,
    view = 'schools',
  ): Observable<[AnalyticsSchool[], number, Facets]> {
    return this.apiGatewayService
      .get(
        `analytics/v3/indexes/esd_wra_edu/views/${view}/query?page=${page}&limit=100${this.queryString(
          query,
        )}`,
      )
      .pipe(
        // @ts-ignore
        take(1),
        map((response: AnalyticsCollectionResponse<AnalyticsSchool>) => {
          const payload = AnalyticsService.normalizeAnalyticsCollectionResponse(response);

          return [
            payload.hits,
            payload.totalHits,
            AnalyticsService.transformFacets(payload.facets),
          ];
        }),
      );
  }

  private queryString(query: AnalyticsQuery): string {
    return query.toString() ? `&${query}` : '';
  }

  searchOrganization(keyword: string): Observable<Organization[]> {
    return this.apiGatewayService
      .get<AnalyticsCollectionResponse<Organization>>(
        `analytics/v3/indexes/esd_wra_edu/views/org_codes/query?search=${encodeURIComponent(keyword)}`,
      )
      .pipe(
        map(
          (response: AnalyticsCollectionResponse<Organization>) =>
            AnalyticsService.normalizeAnalyticsCollectionResponse(response).hits,
        ),
      );
  }

  getOrganizations(orgs: string[]): Observable<Organization[]> {
    return this.apiGatewayService
      .get<AnalyticsCollectionResponse<Organization>>(
        `analytics/v3/indexes/esd_wra_edu/views/org_codes/query?organization=${orgs.toString()}`,
      )
      .pipe(
        map(
          (response: AnalyticsCollectionResponse<Organization>) =>
            AnalyticsService.normalizeAnalyticsCollectionResponse(response).hits,
        ),
      );
  }

  getStudentPlaytime(orgId: string, studentId: string): Observable<ParentHomeDocument> {
    return this.apiGatewayService.get(
      `analytics/v3/indexes/esd_wra_users/documents/${orgId}_${studentId}`,
    );
  }
}
